import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Page } from "../components/AppShell";
import {
  Badge,
  Banner,
  Card,
  DateRangeFilter,
  Pager,
  PanelState,
  windowLabel,
} from "../components/ui";
import { downloadFile } from "../lib/api";
import { date, dateTime, num, relative, titleCase } from "../lib/format";
import { useApi } from "../lib/useApi";
import type { UsersResponse } from "../lib/types";

const PROFILE_TYPES = [
  "HOME_OWNER",
  "INTERIOR_DESIGNER",
  "RETAILER",
  "DEALER",
  "ARCHITECT",
  "CONTRACTOR",
  "PAINTER",
  "OTHER",
];

/**
 * ── Why the search box only takes three things ─────────────────────────────
 * Names, emails and phone numbers are AES-GCM ciphertext with a fresh IV per
 * row, so no two rows holding "Sharma" share a single byte and there is no
 * LIKE to run. What survives is the blind indexes the schema carries: an exact
 * phone number, an exact email, or the id off a support ticket.
 *
 * The API says so explicitly (`unsearchable: true`) rather than returning an
 * empty list, and this page repeats it — an empty table reads as "no such
 * customer", which is a very different and much more alarming fact than "you
 * can't search by name".
 */
export function CustomersPage() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [profileType, setProfileType] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [sort, setSort] = useState<"recent" | "renders" | "oldest">("recent");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // ── No preset is pressed by default here, and that is deliberate ─────────
  // On Leads a window is the whole point of the page. This page is the
  // customer directory: defaulting it to "last 30 days" would hide every
  // account older than a month behind a control nobody touched. Empty dates
  // mean every customer, and `days` only exists so the preset buttons have
  // something to set.
  const [days, setDays] = useState(0);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // Debounced so a typed phone number is one request, not eleven.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  /** What both the table and the export are scoped to. */
  const filters = {
    // Only sent when a preset is actually pressed. A bare request is every
    // customer; see the note on `directoryFilters` in the API.
    days: days || undefined,
    search: search || undefined,
    profileType: profileType || undefined,
    activeOnly: activeOnly ? "1" : undefined,
    from: from || undefined,
    to: to || undefined,
    sort,
  };

  const { data, error, loading, stale } = useApi<UsersResponse>(
    "/api/admin/users",
    { ...filters, page, pageSize: 25 },
  );

  // Read off the response, not off local state: if the two ever disagree,
  // the response is the one that produced the rows on screen.
  const applied = data?.window
    ? windowLabel(data.window.from, data.window.to, data.window.timezone)
    : null;

  /**
   * The file matches the screen, not the page of it.
   *
   * Every filter above is sent along — search, type, the activity checkbox,
   * the signup-date window, the sort — and `page` deliberately is not: an
   * export that handed back only the 25 rows currently visible would be the
   * same quiet wrong as a truncated one. The API streams every matching row.
   */
  async function exportCsv() {
    setExporting(true);
    setExportError(null);
    try {
      await downloadFile(
        "/api/admin/users.csv",
        // Named for the window it covers, so two months' exports do not
        // collide in Downloads — and taken from what the API said it applied,
        // since `downloadFile` names the file client-side and would otherwise
        // be free to disagree with the contents.
        `jkmaxx-customers-${
          data?.window?.slug ?? new Date().toISOString().slice(0, 10)
        }.csv`,
        filters,
      );
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "The export failed.");
    } finally {
      setExporting(false);
    }
  }

  return (
    <Page
      title="Customers"
      subtitle={
        data
          ? `${num(data.pagination.total)} matching accounts${
              applied ? ` · signed up ${applied}` : ""
            }`
          : "Loading…"
      }
      actions={
        <button
          className="btn btn-primary btn-sm"
          onClick={() => void exportCsv()}
          // Nothing to export when the search term was not something the
          // directory can look up — the file would be empty and the rate
          // limit would still have been spent on it.
          disabled={exporting || data?.unsearchable}
        >
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
      }
    >
      <div className="filters">
        {/* Filters on SIGNED UP, not last seen — the column this table
            already shows, and what a dated customer report usually means.
            "Who was active in March" is the Leads page's question. */}
        <DateRangeFilter
          allowAllTime
          days={days}
          from={from}
          to={to}
          onChange={(next) => {
            setDays(next.days);
            setFrom(next.from);
            setTo(next.to);
            setPage(1);
          }}
        />
        <input
          className="input"
          style={{ minWidth: 260 }}
          placeholder="Phone, email or customer id"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search customers"
        />
        <select
          className="select"
          value={profileType}
          onChange={(e) => {
            setProfileType(e.target.value);
            setPage(1);
          }}
          aria-label="Filter by customer type"
        >
          <option value="">All types</option>
          {PROFILE_TYPES.map((t) => (
            <option key={t} value={t}>
              {titleCase(t)}
            </option>
          ))}
        </select>
        <select
          className="select"
          value={sort}
          onChange={(e) => setSort(e.target.value as typeof sort)}
          aria-label="Sort"
        >
          <option value="recent">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="renders">Most rooms</option>
        </select>
        <label
          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}
        >
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => {
              setActiveOnly(e.target.checked);
              setPage(1);
            }}
          />
          Has generated a view
        </label>
      </div>

      {/* Same warning as the lead export, in the same words, because it is
          the same file with a wider net — see the note on LeadsPage. */}
      <Banner tone="warning" title="The export carries personal data">
        The CSV contains customers' names, phone numbers, emails and pincodes
        in plain text, for every account matching the filters below — not just
        the page on screen.{" "}
        {applied ? (
          <>
            This one covers accounts that signed up{" "}
            <strong>{applied}</strong>.
          </>
        ) : (
          <>
            With no dates set this is <strong>every customer on record</strong>
            ; pick a date range to export a smaller slice.
          </>
        )}{" "}
        It leaves this console the moment it is downloaded, and every export is
        recorded in the audit log against your account. Share it the way you
        would share a customer list, because that is what it is.
      </Banner>

      {exportError && <Banner tone="critical">{exportError}</Banner>}

      {data?.unsearchable && (
        <Banner tone="warning" title="That isn't something we can search on">
          Customer names, emails and phone numbers are encrypted individually,
          so there is no partial-text search to run — two accounts named
          "Sharma" share no bytes in the database. Search takes an exact phone
          number (<span className="mono">9876543210</span> or{" "}
          <span className="mono">+919876543210</span>), an exact email address,
          or a customer id.
        </Banner>
      )}

      <Card>
        <div className={stale ? "stale" : undefined}>
          <PanelState
            loading={loading}
            error={error}
            empty={data?.rows.length === 0 && !data?.unsearchable}
            emptyText="No customers match those filters."
          >
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Type</th>
                    <th>Pincode</th>
                    <th className="num">Rooms</th>
                    <th className="num">Views</th>
                    <th className="num">Hearted</th>
                    <th className="num">Devices</th>
                    <th>Signed up</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <Link
                          to={`/customers/${row.id}`}
                          className="cell-strong"
                          style={{ textDecoration: "underline" }}
                        >
                          {row.fullName ?? "Unnamed"}
                        </Link>
                        <div className="cell-mute mono">{row.phone}</div>
                        {row.email && (
                          <div className="cell-mute">{row.email}</div>
                        )}
                        {/* A blocked account is otherwise indistinguishable
                            from an idle one here, which is how somebody ends
                            up twenty minutes into a support ticket before
                            noticing. See /suspicious for the full list. */}
                        {row.block.blocked && (
                          <div style={{ marginTop: 4 }}>
                            <Badge
                              tone={
                                row.block.permanent ? "critical" : "warning"
                              }
                            >
                              {row.block.permanent
                                ? "Blocked"
                                : `Suspended until ${dateTime(row.block.until)}`}
                            </Badge>
                          </div>
                        )}
                      </td>
                      <td className="cell-mute">
                        {titleCase(row.profileType)}
                        {!row.onboarded && (
                          <div>
                            <Badge tone="warning">Incomplete</Badge>
                          </div>
                        )}
                      </td>
                      <td className="mono cell-mute">{row.pincode ?? "—"}</td>
                      <td className="num">{num(row.projects)}</td>
                      <td className="num cell-strong">{num(row.renders)}</td>
                      <td className="num">{num(row.favorites)}</td>
                      <td className="num">
                        {row.liveSessions > 0 ? (
                          <Badge>{row.liveSessions}</Badge>
                        ) : (
                          <span className="cell-mute">0</span>
                        )}
                      </td>
                      <td className="cell-mute">{date(row.createdAt)}</td>
                      <td className="cell-mute">{relative(row.lastLoginAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pager
              meta={data?.pagination}
              onChange={setPage}
              busy={stale}
              label="customers"
            />
          </PanelState>
        </div>
      </Card>
    </Page>
  );
}
